import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefObject } from 'preact';
import { useContext, useEffect, useRef } from 'preact/hooks';

import { getAnnouncementsQueryOptions } from '@/queries/announcements';
import { readAllNotificationsMutationFn } from '@/queries/notifications';
import { useSelector } from '@/redux/helper';
import { atom, useAtom } from '@/shared/atom';
import { GlobalsContext } from '@/shared/context';

// Use atom to store the unread notifications count for smoother updates
const unreadAtom = atom(0);
export const useUnreadBellNotificationCount = () => {
  const userId = useSelector((state) => state.user.userId);
  const [unread, setUnread] = useAtom(unreadAtom);
  const { socket, userSocket } = useContext(GlobalsContext);

  useEffect(() => {
    const socketHandler = ({ unread }: { unread: number }) => {
      setUnread(unread);
    };

    if (!socket || !userSocket || !userId) {
      return undefined;
    }

    userSocket.on('receiveNotifications', socketHandler);
    socket.emit('getNotifications', { userId });

    return () => {
      userSocket.off('receiveNotifications', socketHandler);
    };
  }, [socket, userSocket, userId, setUnread]);

  return unread;
};

export const useReadAllNotifications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: readAllNotificationsMutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getAnnouncementsQueryOptions().queryKey,
      });
    },
  });
};

const isBellRungAtom = atom(false);

export const useShouldBellRing = (unread: number, ref: RefObject<HTMLDivElement>) => {
  const [isBellRung, setIsBellRung] = useAtom(isBellRungAtom);
  const previousUnread = useRef<number>();

  useEffect(() => {
    const { current: element } = ref;
    if (!element) {
      return undefined;
    }
    const handler = () => {
      setIsBellRung(true);
    };
    element.addEventListener('animationend', handler);
    return () => {
      element.removeEventListener('animationend', handler);
    };
  }, [ref, setIsBellRung]);

  useEffect(() => {
    if (previousUnread.current != null && unread > previousUnread.current) {
      setIsBellRung(false);
    }
    previousUnread.current = unread;
  }, [unread, setIsBellRung]);

  return !isBellRung;
};
